import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import InvoiceKeyScanner from '~/components/CartDetailsComponents/InvoiceKeyScanner';
import api from '~/services/api';
import {
  extractInvoiceKey,
  formatInvoiceKey,
  isValidInvoiceKey,
  onlyDigits,
  parseInvoiceKey,
  INVOICE_KEY_LENGTH,
} from '~/util/invoiceKey';

const MODEL_LABELS = { 55: 'NF-e', 65: 'NFC-e' };

/**
 * Chave da NF-e/NFC-e do pedido: digitar/colar, ler QR Code ou código de
 * barras pela câmera, ou importar o XML (só para extrair a chave).
 * Salva em PUT /correios/prepostagem/{cartId}/invoice-key.
 */
export default function InvoiceKeyInput({ cart, onSaved }) {
  const savedKey = cart?.nfce?.key || '';

  const [key, setKey] = useState(savedKey);
  const [edit, setEdit] = useState(!savedKey);
  const [saving, setSaving] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    setKey(savedKey);
    setEdit(!savedKey);
  }, [savedKey]);

  const digits = onlyDigits(key);
  const isComplete = digits.length === INVOICE_KEY_LENGTH;
  const isValid = isComplete && isValidInvoiceKey(digits);
  const parsed = isValid ? parseInvoiceKey(digits) : null;

  const applyExtracted = text => {
    const found = extractInvoiceKey(text);

    if (!found) return false;

    setKey(found);
    setEdit(true);

    return true;
  };

  const handleDetected = useCallback(text => {
    if (applyExtracted(text)) setScannerOpen(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScannerError = useCallback(() => {
    toast.error('Não foi possível acessar a câmera.');
    setScannerOpen(false);
  }, []);

  const handleXmlFile = async e => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';

    if (!file) return;

    try {
      const text = await file.text();

      if (!applyExtracted(text)) {
        toast.error('Não foi possível encontrar a chave da nota neste XML.');
      }
    } catch (err) {
      toast.error('Não foi possível ler o arquivo XML.');
    }
  };

  const handleSave = async () => {
    if (!isValid) {
      toast.error('Chave da nota inválida. Confira os 44 dígitos.');
      return;
    }

    try {
      setSaving(true);

      const { data } = await api.put(
        `/correios/prepostagem/${cart.id}/invoice-key`,
        { key: digits }
      );

      toast.success('Chave da nota fiscal salva!');
      setEdit(false);

      if (onSaved) await onSaved(data?.data || null);
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
          'Não foi possível salvar a chave da nota fiscal.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setKey(savedKey);
    setEdit(false);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div>
      <div className="font-size-14 font-weight-600 text-gray-600 mb-1">
        Chave da NF-e / NFC-e
      </div>

      {!edit ? (
        <>
          <div className="font-size-14 text-gray-700">
            {formatInvoiceKey(savedKey)}
          </div>
          {parsed && (
            <div className="text-muted font-size-12 mb-2">
              {MODEL_LABELS[parsed.model]} nº {parsed.number}, série{' '}
              {parsed.series}
            </div>
          )}
          <Button
            type="button"
            color="primary"
            className="new-layout-btn new-layout-btn-transparent main"
            onClick={() => setEdit(true)}
          >
            Editar
          </Button>
        </>
      ) : (
        <>
          <input
            type="text"
            inputMode="numeric"
            className="form-control"
            placeholder="44 dígitos da chave de acesso"
            value={formatInvoiceKey(key)}
            onChange={e => setKey(onlyDigits(e.target.value).slice(0, 44))}
            onKeyDown={handleKeyDown}
          />

          {isComplete && !isValid && (
            <div className="text-danger font-size-12 mt-1">
              Chave inválida: dígito verificador não confere.
            </div>
          )}
          {parsed && (
            <div className="font-size-12 mt-1" style={{ color: '#34c38f' }}>
              {MODEL_LABELS[parsed.model]} nº {parsed.number}, série{' '}
              {parsed.series}
            </div>
          )}

          <div className="d-flex flex-wrap mt-2" style={{ gap: '0.5rem' }}>
            <Button
              type="button"
              color="primary"
              className="new-layout-btn new-layout-btn-transparent main d-inline-flex align-items-center"
              onClick={() => setScannerOpen(true)}
            >
              <i className="las la-camera font-size-18 mr-1" />
              Ler Código
            </Button>
            <Button
              type="button"
              color="primary"
              className="new-layout-btn new-layout-btn-transparent main d-inline-flex align-items-center"
              onClick={() =>
                fileInputRef.current && fileInputRef.current.click()
              }
            >
              <i className="las la-file-code font-size-18 mr-1" />
              Importar XML
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml,text/xml,application/xml"
              className="d-none"
              onChange={handleXmlFile}
            />
            <Button
              type="button"
              color="primary"
              className="new-layout-btn main"
              onClick={handleSave}
              disabled={!isValid || saving}
            >
              {saving ? 'Salvando...' : 'Salvar chave'}
            </Button>
            {savedKey && (
              <Button
                type="button"
                color="secondary"
                className="new-layout-btn main"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancelar
              </Button>
            )}
          </div>
        </>
      )}

      <Modal isOpen={scannerOpen} toggle={() => setScannerOpen(false)}>
        <ModalHeader toggle={() => setScannerOpen(false)}>
          Ler chave da nota
        </ModalHeader>
        <ModalBody className="text-center">
          <p className="text-muted font-size-13 mb-2">
            Aponte a câmera para o QR Code da NFC-e ou para o código de barras
            da DANFE.
          </p>
          {scannerOpen && (
            <InvoiceKeyScanner
              onDetected={handleDetected}
              onError={handleScannerError}
            />
          )}
        </ModalBody>
      </Modal>
    </div>
  );
}
